import type { z } from 'zod';

import { AccountRefSchema } from '../../users/account-ref/account-ref';

import { ReportSchema } from '../report/report';

/**
 * A queue row: the report, plus both accounts resolved.
 *
 * `Report` carries `reporterUserId` and `reportedUserId` and nothing else,
 * because it is the shape the *member's* side and the repository speak. A staff
 * queue cannot be read that way — a uuid is not a person, and the reviewer needs
 * two facts a uuid cannot give: whether the reported member is **already out of
 * service** (a ban makes most complaints moot, and `statusReason` says which
 * kind), and whether the same **reporter** keeps coming back. Those are exactly
 * the four fields {@link AccountRefSchema} exists to carry, and its own docblock
 * is where the argument for not widening them lives.
 *
 * `access.users` belongs to the `users` module, so the lookup crosses a boundary
 * and goes over the QueryBus (N6), batched once per page rather than once per row
 * (P14b) — this contract is the shape that comes back out of that, never a joined
 * row. It is the same extension `SignalMatchDetailSchema` and
 * `VerificationCaseDetailSchema` already make, for the same reason.
 *
 * **Both sides are nullable, for ONE reason** — unlike Trust, where a blocked
 * registration genuinely has no subject. Here both columns are `notNull`, so a
 * `null` can only mean the lookup came back short: a hard-deleted account, or a
 * users module answering for fewer ids than were asked. The row is still a report
 * somebody must decide, so the queue draws "no longer available" rather than
 * letting a mapper throw and take the whole page with it.
 */
export const ReportDetailSchema = ReportSchema.extend({
  reported: AccountRefSchema.nullable(),
  reporter: AccountRefSchema.nullable(),
}).strict();

export type ReportDetail = z.infer<typeof ReportDetailSchema>;
